import React, { useEffect, useRef, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Select from "react-select";
import Dashboard from "./Dashboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactToPrint from "react-to-print";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL;

const MomSummaryTable = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totals, setTotals] = useState({ yes: 0, no: 0 });
  const [pcFilter, setPcFilter] = useState([]);
  const [constituencyFilter, setConstituencyFilter] = useState([]);
  const [reportingManagerFilter, setReportingManagerFilter] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userNames, setUserNames] = useState([]);
  const [userNameFilter, setUserNameFilter] = useState([]);
  const [pcs, setPcs] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [reportingManager, setReportingManager] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [location, setLocation] = useState("");
  const componentRef = useRef();

  // Users mapped to the Andhra Pradesh project should not see the
  // Non-SHS Meeting column.
  useEffect(() => {
    localforage.getItem("location").then((loc) => {
      if (loc) setLocation(loc);
    });
  }, []);
  const isAndhraPradesh = location === "Andhra Pradesh";
  const isPunjab = location === "Punjab";

  const sortData = (data, field, order) => {
    return [...data].sort((a, b) => {
      const aVal = (a[field] || "").toLowerCase();
      const bVal = (b[field] || "").toLowerCase();

      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        const token = await localforage.getItem("token");
        const res = await api.get(`${API_URL}/new-mom/get-mom`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          },
        });

        let data = res.data.data || [];

        // Also fold in the new-format meetings (kept separate on the
        // backend so the legacy report data is never disturbed).
        try {
          const fmtRes = await api.get(`${API_URL}/mom-format/summary`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fromDate: fromDate || undefined,
              toDate: toDate || undefined,
            },
          });
          if (fmtRes.data && Array.isArray(fmtRes.data.data)) {
            data = [...data, ...fmtRes.data.data];
          }
        } catch (e) {
          // new-format summary is optional; ignore if unavailable
        }

        const grouped = {};
        let yesCount = 0;
        let noCount = 0;
        const uniquePcs = new Set();
        const uniqueConstituencies = new Set();
        const uniqueReportingManager = new Set();
        const uniqueUserNames = new Set();

        data.forEach((item) => {
          const firstCol = isPunjab ? item.district : item.pc;
          const key = `${firstCol}-${item.constituency}-${item.userName}`;
          uniquePcs.add(firstCol);
          uniqueConstituencies.add(item.constituency);
          uniqueReportingManager.add(item.reportingManager);
          uniqueUserNames.add(item.userName || "Unknown");

          if (!grouped[key]) {
            grouped[key] = {
              pc: item.pc,
              district: item.district || "",
              region: item.region || "",
              constituency: item.constituency,
              reportingManager: item.reportingManager,
              userName: item.userName || "Unknown",
              yes: 0,
              no: 0,
              nonShsCount: 0,
              dom: item.dom,
            };
          }

          grouped[key].yes += item.yes || 0;
          grouped[key].no += item.no || 0;
          grouped[key].nonShsCount += item.nonShsCount || 0;

          yesCount += item.yes || 0;
          noCount += item.no || 0;
        });

        const summaryArray = Object.values(grouped);

        setSummaryData(summaryArray);
        setFilteredData(summaryArray);
        const totalNonShsCount = summaryArray.reduce(
          (acc, item) => acc + item.nonShsCount,
          0
        );

        setTotals({
          yes: yesCount,
          no: noCount,
          nonShsCount: totalNonShsCount,
        });

        setPcs([...uniquePcs].map((pc) => ({ value: pc, label: pc })));
        setConstituencies(
          [...uniqueConstituencies].map((c) => ({ value: c, label: c }))
        );
        setReportingManager(
          [...uniqueReportingManager].map((r) => ({ value: r, label: r }))
        );
        setUserNames([...uniqueUserNames].map((u) => ({ value: u, label: u })));
      } catch (err) {
        console.error("Error fetching MOM data:", err.message);
      }
    };

    fetchFilteredData();
  }, [fromDate, toDate]); // trigger API call when date range changes

  useEffect(() => {
    let filtered = summaryData;
    let yesCount = 0;
    let noCount = 0;
    let nonShsTotal = 0;

    if (pcFilter.length > 0) {
      const selectedPCs = pcFilter.map((item) => item.value);
      filtered = filtered.filter((item) =>
        selectedPCs.includes(isPunjab ? item.district : item.pc)
      );
    }
    if (constituencyFilter.length > 0) {
      const selectedACs = constituencyFilter.map((item) => item.value);
      filtered = filtered.filter((item) =>
        selectedACs.includes(item.constituency)
      );
    }
    if (reportingManagerFilter.length > 0) {
      const selectedReportingManagers = reportingManagerFilter.map(
        (item) => item.value
      );
      filtered = filtered.filter((item) =>
        selectedReportingManagers.includes(item.reportingManager)
      );
    }
    if (fromDate) {
      filtered = filtered.filter((item) => item.dom >= fromDate);
    }
    if (toDate) {
      filtered = filtered.filter((item) => item.dom <= toDate);
    }

    if (userNameFilter.length > 0) {
      const selectedUserNames = userNameFilter.map((item) => item.value);
      filtered = filtered.filter((item) =>
        selectedUserNames.includes(item.userName)
      );
    }

    // 🔁 Now apply sorting on the filtered data
    if (sortField) {
      filtered = sortData(filtered, sortField, sortOrder);
    }

    // Update totals after filtering
    filtered.forEach((item) => {
      yesCount += item.yes;
      noCount += item.no;
      nonShsTotal += item.nonShsCount || 0;
    });

    setFilteredData(filtered);
    setTotals({ yes: yesCount, no: noCount, nonShsCount: nonShsTotal });
  }, [
    userNameFilter,
    pcFilter,
    constituencyFilter,
    fromDate,
    toDate,
    summaryData,
    reportingManagerFilter,
    sortField,
    sortOrder,
  ]);

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  return (
    <>
      <Dashboard />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "20px",
          marginTop: "20px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <label style={{ width: "300px" }}>
          {isPunjab ? "District:" : "Parliamentary Constituency:"}
          <Select
            isMulti
            options={pcs}
            value={pcFilter}
            onChange={setPcFilter}
            placeholder={isPunjab ? "Select District" : "Select PC"}
          />
        </label>

        <label style={{ width: "300px" }}>
          Assembly Constituency:
          <Select
            isMulti
            options={constituencies}
            value={constituencyFilter}
            onChange={setConstituencyFilter}
            placeholder="Select AC"
          />
        </label>
        <label style={{ width: "300px" }}>
          Reporting Manager:
          <Select
            isMulti
            options={reportingManager}
            value={reportingManagerFilter}
            onChange={setReportingManagerFilter}
            placeholder="Select Reporting Manager"
          />
        </label>
        <label style={{ width: "300px" }}>
          User Name:
          <Select
            isMulti
            options={userNames}
            value={userNameFilter}
            onChange={setUserNameFilter}
            placeholder="Select User"
          />
        </label>

        <label>
          From Date:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>

        <label>
          To Date:
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>

        <ReactToPrint
          trigger={() => (
            <FontAwesomeIcon icon={faFilePdf} className="font-pdf" size="2x" />
          )}
          content={() => componentRef.current}
          pageStyle={`@page { margin: 5mm 7mm; }`}
          documentTitle={`Leader Meeting Report`}
          removeAfterPrint={true}
        />
      </div>

      <div className="intervention-table">
        <h2>Leader Meeting Report</h2>
        <table
          className="mom-table"
          id="pdf-content"
          ref={componentRef}
          style={{ margin: "20px auto 0" }}
        >
          <thead>
            <tr>
              {/* <th>Recorded Date and Time</th> */}
              <th>{isPunjab ? "District" : "Parliamentary Constituency"}</th>
              <th>Assembly Constituency</th>
              <th
                onClick={() => handleSort("reportingManager")}
                style={{ cursor: "pointer" }}
              >
                Reporting Manager{" "}
                {sortField === "reportingManager" &&
                  (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Submitted By</th>
              <th>Total Meeting Recorded</th>
              <th>MoM Submitted</th>
              {!isAndhraPradesh && !isPunjab && <th>Non-SHS Meeting</th>}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, index) => (
              <tr key={index}>
                {/* <td>
                  {new Date(row.createdAt).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </td> */}
                <td>{isPunjab ? row.district : row.pc}</td>
                <td>{row.constituency}</td>
                <td>{row.reportingManager}</td>
                <td>{row.userName}</td>
                <td>{row.yes + row.no}</td>
                <td>{row.yes}</td>
                {!isAndhraPradesh && !isPunjab && <td>{row.nonShsCount}</td>}
              </tr>
            ))}
            <tr style={{ backgroundColor: "#74a7e9" }}>
              <td colSpan="4">
                <strong>Grand Total</strong>
              </td>
              <td>
                <strong>{totals.yes + totals.no}</strong>
              </td>
              <td>
                <strong>{totals.yes}</strong>
              </td>
              {!isAndhraPradesh && (
                <td>
                  <strong>{totals.nonShsCount || 0}</strong>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MomSummaryTable;
